
import {
  defineComponent, ref, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Tooltip from 'primevue/tooltip';
import { OVERRIDABLE_FIELDS } from '@/composables/custom-data/overridableSystemFields';
import { Field, ForeignKeyRow } from '@/composables/custom-data/customData.interfaces';
import { useStore } from '@/store';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'SchemaOverridesEditor',

  components: {
    BaseDropdown,
    Button,
    Dialog,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    idRelation: {
      type: Number,
      required: true,
    },
  },

  setup(props: { idRelation: number }) {
    const { t } = useI18n();
    const store = useStore();
    const idCustomDataDefinition = computed(() => store.getters['customObject/getIdCustomDataDefinition']);
    const fields = computed(() => store.getters['customObject/getFields']);
    const relationships = computed(() => store.getters['customObject/getRelationships']);
    const currentEditingElement = computed(() => store.getters['customObject/getCurrentEditingElement']);
    const relationshipData = computed(() => store.getters['customObject/getRelationById'](props.idRelation));
    const overrides = computed(() => store.getters['customObject/getOverrides']);

    const selectedCustomField = ref<string>('');
    const selectedSystemField = ref<string>('');
    const overridesToManagePriority = ref<any>(null);
    const priorityModalVisible = ref<boolean>(false);

    const showAddingOverrideForm = computed(() => (currentEditingElement.value
      && currentEditingElement.value.elementType === 'override'));

    const currentRelationOverrides = computed(() => overrides.value.filter((override: any) => override.target_system_schema === relationshipData.value.schema
     && parseInt(override.id_custom_data_definition, 10) === parseInt(idCustomDataDefinition.value, 10)));

    const customOverrideFieldsOptions = computed(() => fields.value
      .filter((f: Field) => !f.unique_key
          && !relationships.value.some((rel: ForeignKeyRow) => rel.localField === f.name)
          && (!currentRelationOverrides.value.some((override: any) => override.custom_field_name === f.name)
           || (selectedCustomField.value && f.name === selectedCustomField.value)))
      .map((f: Field) => ({ value: f.name, label: f.name })));

    const systemOverrideFieldsOptions = computed(() => {
      if (!selectedCustomField.value || selectedCustomField.value === '') return [];

      const customField = fields.value.find((f: Field) => f.name === selectedCustomField.value);
      if (!customField) return [];

      const targetSchema = relationshipData.value.schema;
      if (!targetSchema) return [];

      const overridableFields = OVERRIDABLE_FIELDS[targetSchema as keyof typeof OVERRIDABLE_FIELDS] || [];

      return overridableFields
        .filter((sf: any) => {
          const systemFieldTypes = Array.isArray(sf.type) ? sf.type : [sf.type];
          return systemFieldTypes.includes(customField.type as string);
        })
        .filter((sf: any) => !currentRelationOverrides.value.some((override: any) => override.system_field_name === sf.field))
        .map((sf: any) => ({ value: sf.field, label: sf.label }));
    });

    const onRemoveOverride = (override: any) => {
      store.dispatch('customObject/handleOverrideAction', {
        action: 'remove',
        id: override ? override.id : null,
      });
    };

    const addOverride = () => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.overrideEditor.messages.cannotAddWhileEditing'), 4000);
        return;
      }
      selectedCustomField.value = '';
      selectedSystemField.value = '';
      store.dispatch('customObject/handleOverrideAction', {
        action: 'add-new-empty',
      });
    };

    const onSaveOverride = () => {
      if (!selectedCustomField.value || selectedCustomField.value === '' || !selectedSystemField.value || selectedSystemField.value === '') {
        showToastError(t('customData.overrideEditor.messages.selectFieldsRequired'), 4000);
        return;
      }
      const itemOverride = {
        id: currentEditingElement.value.id,
        custom_field_name: selectedCustomField.value,
        system_field_name: selectedSystemField.value,
        priority: 0,
        target_system_schema: relationshipData.value.schema,
        id_custom_data_definition: idCustomDataDefinition.value,
      };
      store.dispatch('customObject/handleOverrideAction', {
        action: 'save',
        overrideItem: itemOverride,
      });
    };

    const openPriorityModal = (override: any) => {
      const clonedOverrides = JSON.parse(JSON.stringify(overrides.value));
      overridesToManagePriority.value = clonedOverrides.filter((o: any) => o.target_system_schema === relationshipData.value.schema
        && o.system_field_name === override.system_field_name);
      overridesToManagePriority.value.sort((a: any, b: any) => a.priority - b.priority);
      priorityModalVisible.value = true;
    };

    const movePriority = (index: number, direction: 'up' | 'down') => {
      const clonedOverrides = JSON.parse(JSON.stringify(overridesToManagePriority.value));
      const temp = clonedOverrides[index];
      if (direction === 'up') {
        clonedOverrides[index] = clonedOverrides[index - 1];
        clonedOverrides[index - 1] = temp;
      } else {
        clonedOverrides[index] = clonedOverrides[index + 1];
        clonedOverrides[index + 1] = temp;
      }
      const updatedOverrides = clonedOverrides.map((o: any, index2: number) => ({
        ...o,
        priority: index2 + 1,
      }));
      overridesToManagePriority.value = updatedOverrides;
    };

    const savePriorities = () => {
      store.dispatch('customObject/handleOverrideAction', {
        action: 'save-priorities',
        overridesPriority: overridesToManagePriority.value,
      });
      overridesToManagePriority.value = null;
      priorityModalVisible.value = false;
    };

    const getCustomSchemaName = (id: number) => {
      if (parseInt(id.toString(), 10) === 0) {
        const currentObjectName = store.getters['customObject/getCurrentObjectName'];
        return currentObjectName && currentObjectName !== '' ? currentObjectName : t('customData.overrideEditor.defaults.currentObject');
      }
      const customSchema = store.getters['customObject/getCustomSchemaById'](id);
      return customSchema ? customSchema.label : '';
    };

    return {
      t,
      currentRelationOverrides,
      selectedCustomField,
      selectedSystemField,
      showAddingOverrideForm,
      onRemoveOverride,
      customOverrideFieldsOptions,
      systemOverrideFieldsOptions,
      addOverride,
      onSaveOverride,
      openPriorityModal,
      overridesToManagePriority,
      priorityModalVisible,
      idCustomDataDefinition,
      movePriority,
      savePriorities,
      getCustomSchemaName,
    };
  },
});
